import {
  type GridColumnProps,
  type GridRowClickEvent,
} from "@progress/kendo-react-grid";
import { useRouteContext } from "@tanstack/react-router";
import { useState } from "react";

import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import { CHLD } from "@/models/chld";
import { type FAMI } from "@/models/fami";
import { FiPlus } from "react-icons/fi";
import { formatDateOnly } from "@/utils/dates";
import {
  createNamedFilename,
  CSVDownloadButton,
} from "@/components/shared/CSVDownloadButton";
import FAMIEditDialog from "./dialogs/FAMIEditDialog";

import {
  buildFamilyAddDefaults,
  getFamilyFicnOptions,
  getMostRecentFamilyForFicn,
} from "./useFAMIFormLogic";
import FAMIFicnPicker from "./dialogs/FAMIFicnPicker";

const columns: GridColumnProps[] = [
  { id: "ProgramYear", field: "ProgramYear", title: "Program Year" },
  { id: "PreschoolName", field: "PreschoolName", title: "School" },
  { id: "LocalFamilyId", field: "LocalFamilyId", title: "FICN" },
  {
    id: "HeadOfHousehold1",
    field: "HeadOfHousehold1",
    title: "Head Of Household 1",
  },
  {
    id: "HeadOfHousehold2",
    field: "HeadOfHousehold2",
    title: "Head Of Household 2",
  },
  {
    id: "EffectiveStartDate",
    field: "EffectiveStartDate",
    title: "Effective Start Date",
  },
];

const csvHeaders = [
  { label: "Program Year", key: "ProgramYear" },
  { label: "School", key: "PreschoolName" },
  { label: "FICN", key: "LocalFamilyId" },
  { label: "Head Of Household 1", key: "HeadOfHousehold1" },
  { label: "Head Of Household 2", key: "HeadOfHousehold2" },
  { label: "Effective Start Date", key: "EffectiveStartDate" },
];

export default function FAMIRecordsTab({
  famiRecords,
  child,
}: {
  famiRecords: FAMI[];
  child: CHLD;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [popupFamily, setPopupFamily] = useState<FAMI | undefined>();
  const [familyDefaults, setFamilyDefaults] = useState<
    Partial<FAMI> | undefined
  >();

  const canEdit = leaContext.hasRole("FAMIEdit");

  const blankDefaults: Partial<FAMI> = {
    LocalChildId: child.LocalChildId ?? "",
    PreschoolCode: child.PreschoolCode ?? "",
    ProgramYear: child.ProgramYear ?? "",
    CSPPID: child.CSPPID ?? "",
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setPickerOpen(false);
    setPopupFamily(undefined);
    setFamilyDefaults(undefined);
  };

  const openAddForFicn = (ficn: string) => {
    const mostRecent = getMostRecentFamilyForFicn(famiRecords, ficn);
    const defaults: Partial<FAMI> = mostRecent
      ? buildFamilyAddDefaults(mostRecent)
      : { ...blankDefaults, LocalFamilyId: ficn };
    setFamilyDefaults(defaults);
    setPopupFamily(undefined);
    setPickerOpen(false);
    setDialogOpen(true);
  };

  const openAddBlank = () => {
    setFamilyDefaults(blankDefaults);
    setPopupFamily(undefined);
    setPickerOpen(false);
    setDialogOpen(true);
  };

  const onAddButtonClick = () => {
    const ficnOptions = getFamilyFicnOptions(famiRecords);

    if (ficnOptions.length === 0) {
      openAddBlank();
      return;
    }

    setPickerOpen(true);
  };

  const onEditButtonClick = (f: FAMI) => {
    setFamilyDefaults(undefined);
    setPopupFamily(f);
    setDialogOpen(true);
  };

  const childName = [
    child.ChildFirstName,
    child.ChildMiddleName,
    child.ChildLastName,
  ]
    .filter(Boolean)
    .join(" ");

  const tableData = famiRecords.map((record) => ({
    ...record,
    ChildName: childName,
    PreschoolName: leaContext?.currentOrgPreschools?.find(
      (x) => x.PreschoolCDSCode === record.PreschoolCode
    )?.PreschoolName,
    HeadOfHousehold1: `${record.HeadOfHousehold1FirstName} ${record.HeadOfHousehold1LastName}`,
    HeadOfHousehold2: `${record.HeadOfHousehold2FirstName} ${record.HeadOfHousehold2LastName}`,
    EffectiveStartDate: record.EffectiveStartDate
      ? formatDateOnly(record.EffectiveStartDate)
      : "",
  }));

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <TooltipTrigger tooltipText="Export family records">
          <CSVDownloadButton
            data={tableData}
            headers={csvHeaders}
            filename={createNamedFilename(["ChildName"], "family-record")}
          />
        </TooltipTrigger>
        {canEdit && (
          <TooltipTrigger tooltipText="Add a family record">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onAddButtonClick}
            >
              <FiPlus className="me-1" />
              Add
            </button>
          </TooltipTrigger>
        )}
      </div>

      <KendoTableClientState
        data={tableData}
        columns={columns}
        onRowClick={(event: GridRowClickEvent) => {
          onEditButtonClick(event.dataItem);
        }}
      />

      {pickerOpen && (
        <FAMIFicnPicker
          options={getFamilyFicnOptions(famiRecords)}
          onSelectFicn={openAddForFicn}
          onSelectNewFamily={openAddBlank}
          onClose={closeDialog}
        />
      )}

      {dialogOpen && (
        <FAMIEditDialog
          selectedFamily={popupFamily}
          familyDefaults={familyDefaults}
          onClose={closeDialog}
        />
      )}
    </>
  );
}
